import { cloneDeep } from 'lodash-es';
import { createTimeline, utils } from 'animejs';
import FJMotionData, { STATIC_FULL_DURATION_KEYFRAMES } from './FJMotionData';

function getAnimationTypes(data) {
    let types = {};
    for (let key in data) {
        types[key] = key.toString();
    }
    return types;
}

/**
 * @desc 动画的名字
 */
const ANIMATION_NAME = getAnimationTypes(FJMotionData);
/**
 * @desc 需要动态更新数值的动画
 * @type {*[]}
 */
const DYNAMIC_ANIMATION_NAMES = [
    ANIMATION_NAME.MOVE_IN_LEFT,
    ANIMATION_NAME.MOVE_IN_RIGHT,
    ANIMATION_NAME.MOVE_IN_UP,
    ANIMATION_NAME.MOVE_IN_BOTTOM,
    ANIMATION_NAME.MOVE_IN_LEFT_UP,
    ANIMATION_NAME.MOVE_IN_LEFT_BOTTOM,
    ANIMATION_NAME.MOVE_IN_RIGHT_UP,
    ANIMATION_NAME.MOVE_IN_RIGHT_BOTTOM,
    ANIMATION_NAME.MOVE_OUT_LEFT,
    ANIMATION_NAME.MOVE_OUT_RIGHT,
    ANIMATION_NAME.MOVE_OUT_UP,
    ANIMATION_NAME.MOVE_OUT_BOTTOM,
    ANIMATION_NAME.MOVE_OUT_LEFT_UP,
    ANIMATION_NAME.MOVE_OUT_LEFT_BOTTOM,
    ANIMATION_NAME.MOVE_OUT_RIGHT_UP,
    ANIMATION_NAME.MOVE_OUT_RIGHT_BOTTOM,
    ANIMATION_NAME.TUMBLE_LEFT_IN,
    ANIMATION_NAME.TUMBLE_RIGHT_IN,
    ANIMATION_NAME.TUMBLE_LEFT_OUT,
    ANIMATION_NAME.TUMBLE_RIGHT_OUT,
    ANIMATION_NAME.STOMP_IN,
    ANIMATION_NAME.STOMP_OUT,
    ANIMATION_NAME.SUBTITLE_SCROLL_DOWN_LOOP,
    ANIMATION_NAME.SUBTITLE_SCROLL_DOWN_LOOP_ONCE,
];

/**
 * @desc wipe方式的动画名字
 * @type {string[]}
 */
const WIPE_ANIMATION_NAMES = [
    ANIMATION_NAME.WIPE_IN_LEFT,
    ANIMATION_NAME.WIPE_IN_RIGHT,
    ANIMATION_NAME.WIPE_IN_UP,
    ANIMATION_NAME.WIPE_IN_BOTTOM,
    ANIMATION_NAME.WIPE_OUT_LEFT,
    ANIMATION_NAME.WIPE_OUT_RIGHT,
    ANIMATION_NAME.WIPE_OUT_UP,
    ANIMATION_NAME.WIPE_OUT_BOTTOM,
];

/**
 * @desc 需要设置backfaceVisibility的动画名字
 * @type {string[]}
 */
const BACKFACE_VISIBILITY_ANIMATION_NAMES = [
    ANIMATION_NAME.ALL_FLIP_Y_LOOP,
    ANIMATION_NAME.ALL_FLIP_X_LOOP,
    ANIMATION_NAME.ALL_SPIN_3D_LOOP,
    ANIMATION_NAME.ALL_SPIN_3D_VERTICAL_LOOP,
    ANIMATION_NAME.ALL_SWAY_3D_LOOP,
    ANIMATION_NAME.ALL_SWAY_3D_VERTICAL_LOOP,
    ANIMATION_NAME.ALL_SPIN_SMOOTH_LOOP,
    ANIMATION_NAME.ALL_SPIN_3D_IN,
    ANIMATION_NAME.ALL_SPIN_3D_OUT,
];

const DEFAULT_ANIMATION_DURATION = 10; // 默认动画的时长

class Animation {
    /**
     * @desc 构造函数
     * @param {{}} props
     * @param {boolean} props.autoplay - 是否自动播放
     * @param {boolean | number} props.loop - 循环播放，设置循环播放的次数，如果为true则无限循环播放
     * @param {HTMLElement} props.animationContainer - 做动画的元素的父元素，包含了所有的动画元素
     * @param {string} props.animationInName - 进场动画
     * @param {string} props.animationOutName - 退场动画
     */
    constructor(props = {}) {
        const {
            autoplay = false,
            animationContainer,
            animationTargets = [],
            animationInName,
            animationOutName,
            animationLoopName,
            timeLineTimeData, // FJAnimationTimelineData的实例
            callbacks = {},
            isActive = true,
            textGetAnimationByTimeFun = null,
        } = props;
        this._isActive = isActive;
        this._isLoop = animationLoopName && animationLoopName !== 'None' ? true : false;
        this._animationTargets = animationTargets;
        this._timeLineTimeData = timeLineTimeData;
        this._animationContainer = animationContainer; // 动画元素的父组件
        this._animationInName = animationInName;
        this._animationLoopName = animationLoopName;
        this._animationOutName = animationOutName;
        this._destroyCallbacks = [];
        this._callbacks = callbacks;
        this._activeAnimationName = ''; // 当前动画名称（包含入出场动画+delay前后）
        this._realRenderActiveAnimationName = ''; // 当前真实渲染动画名称,当前动画阶段 参数为动画名或''，为''表示没有任何动画，可以表示静态帧
        // 动画时间线，每个动画有单独的时间线
        this._animationTimeLines = [];
        this._isStaticAnime = false; // 是否是常规整体动画
        this._textGetAnimationByTimeFun = textGetAnimationByTimeFun;
        // 动画控制器
        this._animation = createTimeline({
            autoplay: autoplay,
            onUpdate: anime => {
                this._onUpdate(anime, animationTargets);
            },
            onBeforeUpdate: anime => {
                this._onBeforeUpdate(anime, animationTargets);
            },
            onComplete: anime => {
                this._onComplete(anime, animationTargets);
            },
        });
    }

    get isActive() {
        return this._isActive;
    }
    /**
     * @desc 主动画时间轴更新回调
     * @param {Timeline} anime - 动画
     */
    _onBeforeUpdate(anime, animationTargets) {
        if (this._callbacks.onBeforeUpdate) {
            this._callbacks.onBeforeUpdate(anime);
        } else {
            const currentTime = anime.currentTime;
            const animationName = this.getCurrentActiveAnimationName(currentTime);

            this.onUpdateLoopAnimationRender(currentTime);

            // fix: ID: 1027390
            this._updateStaticTargetsForPerFrame(animationName, animationTargets);

            // 如果anime._currentTime有值，则使用anime._currentTime，否则使用 currentTime  currentTime 是计算后的值
            //  _currentTime 是实际seek 的时间 但在seek 0 时会为空
            const realityCurrentTime = anime._currentTime ? anime._currentTime : currentTime;
            const isStaticAnime = this._isStaticAnimeByTime(realityCurrentTime);
            isStaticAnime && this._updateContainerForBegin(animationTargets, animationName, realityCurrentTime);

            if (this._activeAnimationName === animationName && isStaticAnime === this._isStaticAnime) {
                return;
            }

            this._isClearAnimationFromTarget(realityCurrentTime) && this._clearAnimationFromTarget(animationTargets);
            this._activeAnimationName = animationName;
            this._isStaticAnime = isStaticAnime;
        }
    }

    /**
     * @desc 判断是否需要清空动画样式
     * @param {number} currentTime - 当前时间
     * @returns {boolean} 是否需要清空动画样式
     */
    _isClearAnimationFromTarget(currentTime) {
        // TODO 文字动画和整体动画数据不互通，所以在文字对象中 获取当前时间对应的动画名用 _textGetAnimationByTimeFun 方法
        const curAnimationName = this._textGetAnimationByTimeFun
            ? this._textGetAnimationByTimeFun(currentTime)
            : this.getRealRenderAnimationName(currentTime);

        return curAnimationName !== '' && curAnimationName !== 'None';
    }

    _isStaticAnimeByTime(currentTime) {
        // 只用文字对象有_isWholeAnimationFun方法 其他元素只有静态动画所以这里返回true
        if (!this._textGetAnimationByTimeFun) return true;

        const curAnimationName = this._textGetAnimationByTimeFun(currentTime);

        if (curAnimationName === '') return false;

        return ANIMATION_NAME[curAnimationName] ? true : false;
    }

    /**
     * @desc 主动画时间轴更新回调
     * @param {Timeline} anime - 动画
     * @param {[]} animationTargets - 动画的目标
     */
    _onUpdate(anime, animationTargets) {
        if (this._callbacks.onUpdate) {
            this._callbacks.onUpdate(anime);
        }
    }

    _onComplete(anime, animationTargets) {
        if (this._callbacks.onComplete) {
            this._callbacks.onComplete(anime);
        }
    }

    /**
     * @desc 子动画时间轴更新回调
     * @param {Timeline} anime - 动画
     * @param {[]} animationTargets - 动画的目标
     */
    _childOnUpdate(anime, animationTargets, options = {}) {
        // console.log('childOnUpdate', anime.iterationProgress);
        const currentTime = anime.currentTime;
    }

    /**
     * @desc 子动画时间轴更新回调
     * @param {Timeline} anime - 动画
     * @param {[]} animationTargets - 动画的目标
     */
    _childOnBeforeUpdate(anime, animationTargets, options = {}) {}

    _childOnComplete(anime, animationTargets, options = {}) {
        const { animationName } = options;
    }

    // 特殊的动画的副作用业务代码
    _updateStaticTargetsForPerFrame = (aniName, animationTargets) => {
        if (typeof aniName !== 'string') return;

        if (!aniName.includes('WIPE_EDGE')) {
            animationTargets.forEach(target => {
                if (target.style['clip-path'] !== undefined && target.style['clip-path'] !== '') {
                    target.style['clip-path'] = 'initial';
                }
            });
        }
    };

    /**
     * @desc 更新循环动画的渲染（实时控制循环动画的render）
    //  * @param {number} currentTime - 当前时间
     */
    onUpdateLoopAnimationRender(currentTime) {
        const curRenderAnimationName = this.getRealRenderAnimationName(currentTime);
        // TODO: 这里的优化逻辑屏蔽的原因，循环动画的属性会影响到其他动画，即便不在循环动画的时间内
        // 牺牲这个优化处理 ，
        // if (
        //     (curRenderAnimationName !== this._realRenderActiveAnimationName &&
        //         this._realRenderActiveAnimationName === this._animationLoopName) ||
        //     curRenderAnimationName === this._animationLoopName
        // ) {
        //     this._updateLoopAnimationRender(
        //         curRenderAnimationName === this._animationLoopName,
        //         this._animationOutName !== 'None',
        //     );
        // }
        this._updateLoopAnimationRender(
            curRenderAnimationName === this._animationLoopName,
            this._animationOutName !== 'None',
        );
        this._realRenderActiveAnimationName = curRenderAnimationName;
    }
    getTimeDataAtCurrentTime(currentTime) {
        if (!this._timeLineTimeData) return null;
        const { startTime, endTime } = this._timeLineTimeData;
        // 判断是否在动画时间线内
        if (currentTime < startTime || currentTime > endTime) return null;
        return this._timeLineTimeData.getTimeDataInTime(currentTime);
    }
    /**
     * @desc 获取当前实际渲染的动画名字，用于处理循环动画的渲染
     * @param {number} currentTime - 当前时间
     * @return {string} 当前渲染的动画名字
     */
    getRealRenderAnimationName(currentTime) {
        const data = this.getTimeDataAtCurrentTime(currentTime);
        return data ? data.animationName : '';
    }
    /**
     * @desc 获取当前动画名字(包含delay前后)
     * @param {*} currentTime
     * @returns
     */
    getCurrentActiveAnimationName(currentTime) {
        if (!this._timeLineTimeData) return '';

        const animations = this._timeLineTimeData.animationTimes;
        const allStartTime = this._timeLineTimeData.startTime;
        const allEndTime = this._timeLineTimeData.endTime;

        if (animations.length < 1) return '';

        let currentAnimationName = '';
        const sortedAnimations = animations.sort((a, b) => a.endTime - b.endTime);
        for (let i = 0; i < sortedAnimations.length; i++) {
            const animation = sortedAnimations[i];
            const { startTime, endTime } = animation;

            if (allStartTime === startTime && currentTime < startTime) {
                currentAnimationName = animation.animationName;
                break;
            }

            if (allEndTime === endTime && currentTime > endTime) {
                currentAnimationName = animation.animationName;
                break;
            }

            if (currentTime >= startTime && currentTime <= endTime) {
                currentAnimationName = animation.animationName;
                break;
            }
        }

        return currentAnimationName;
    }

    /**
     * @desc 添加动画
     * @param {[]} targets - 动画的目标,动画目标的样式中的opacity,transformOrigin和transform不能被污染，否则动画的效果可能会出现偏差
     * @param {string} animationName - 动画的名字，单位为毫秒
     * @param {number} duration - 动画的总时长，单位为毫秒
     * @param {number | function} delay - 动画与动画间的间隔，delay < duration，单位为毫秒
     * @param {number} startTime - 动画开始的时长，从动画开始播放算起的，包含了延迟，单位为毫秒
     * @param {number} endTime - 动画结束的时长，从动画开始播放算起的，单位为毫秒
     * @param {*} options - 用于实现一些动态参数的设置
     * @param {{}} options.relativeRect - 必要的方法，获取动画在画布中的相对位置大小
     * @return {boolean} 是否添加成功
     */
    addAnimation(targets, animationName, duration = 1000, delay = 0, startTime = 0, endTime, options = {}) {
        if (!targets || !animationName) return false;
        let animationData = this._createAnimationData(animationName, { ...options, endTime, startTime });
        if (!animationData) return false;
        const loop = animationData.isLoop || false;
        const { methods = {}, perspective } = animationData;
        const { onDestroy, customMotion } = methods;

        const modifyDurationData = this._modifyDuration(animationData, duration);

        // TODO: 这个整体循环动画比较特殊，需要整个动画时长内执行，所以需要修改duration
        if (STATIC_FULL_DURATION_KEYFRAMES.includes(animationName)) {
            modifyDurationData.duration = endTime - startTime;
        }

        let mergeData = {
            duration, // 动画的duration
            ...modifyDurationData, // 处理时间后的duration
            animationName,
            delay,
            loop,
            composition: loop ? 'replace' : 'none', // 避免相同属性的动画叠加或者冲突
            onUpdate: anime => {
                this._childOnUpdate(anime, this._animationTargets, { animationName });
            },
            onBeforeUpdate: anime => {
                this._childOnBeforeUpdate(anime, this._animationTargets, { animationName });
            },
            onComplete: anime => {
                this._childOnComplete(anime, this._animationTargets, { animationName });
            },
        };
        if (onDestroy) {
            const destroy = onDestroy;
            this._destroyCallbacks.push(() => {
                destroy(targets);
            });
        }
        if (perspective) {
            const viewSize = options.viewSize || { width: 1920, height: 1080 };
            mergeData = this._adjustAnimeData(mergeData, viewSize);
        }

        if (customMotion) {
            Object.assign(
                mergeData,
                animationData.methods.customMotion(targets, { delay, duration: mergeData.duration }),
            );
        }
        // TODO 4.0版本的animejs在设置的属性中如果有func，则会依次被多次调用。所以在传入数据时，需要把在数据中的func字段全部删除
        delete mergeData.methods;

        const timeline = createTimeline({
            autoplay: false,
        });

        timeline.add(targets, mergeData, startTime);

        // loop && (timeline.duration = endTime);
        this._animation.sync(timeline, 0);
        this._animationTimeLines.push({
            timeline,
            animationName,
            showDuration: options.elementShowDuration,
            singleDuration: options.singleDuration,
        });

        return true;
    }
    /**
     * @desc 修改动画的duration
     * @param {object} animeData - 动画数据
     * @param {number} duration - 动画的duration
     * @return {object} 修改后的动画数据
     */
    _modifyDuration(animeData, duration) {
        // 如果输入不是对象，直接返回
        if (!animeData || typeof animeData !== 'object') {
            return animeData;
        }

        // 创建对象的深拷贝
        const result = cloneDeep(animeData);
        // 如果对象本身有duration属性，直接修改
        if (result.hasOwnProperty('duration') && typeof result.duration === 'number') {
            result.duration = result.duration * duration;
        }

        // 递归处理对象中的所有属性
        for (const key in result) {
            if (result.hasOwnProperty(key)) {
                const value = result[key];

                // 如果是数组，遍历数组中的每个元素
                if (Array.isArray(value)) {
                    result[key] = value.map(item => this._modifyDuration(item, duration));
                }
                // 如果是对象，递归处理
                else if (value && typeof value === 'object') {
                    result[key] = this._modifyDuration(value, duration);
                }
            }
        }

        return result;
    }

    // 更新动画容器
    _updateContainerForBegin(targets, aniName, currentTime) {
        this._updateTransformOrigin(targets, aniName);
        this._updateAnimationContainerStyle(aniName, currentTime);
    }
    /**
     * @desc 清除动画样式及当前属性，屏蔽动画渲染
     * @param {Timeline} excludeTimeline - 排除的动画
     */
    _clearAnimationsStyle(currentAnimationName) {
        this._animationTimeLines.forEach(data => {
            const { timeline, animationName } = data;
            if (animationName !== currentAnimationName) {
                // 忽略动画的渲染， 处理动画衔接时没有彻底清除动画属性问题,源码上修改，否则循环动画会一直渲染到结束
                if (timeline._head && !timeline._head.fj_isIgnoreRender) {
                    timeline._head.fj_isIgnoreRender = true;
                    utils.cleanInlineStyles(timeline);
                }
            } else {
                delete timeline._head.fj_isIgnoreRender;
            }
        });
    }

    _updateLoopAnimationRender(isRender, isClearStyle = true) {
        if (!this._animationLoopName) return;
        this._animationTimeLines.forEach(data => {
            const { timeline, animationName } = data;
            if (animationName === this._animationLoopName) {
                // 忽略动画的渲染， 处理动画衔接时没有彻底清除动画属性问题,源码上修改，否则循环动画会一直渲染到结束
                if (timeline._head) {
                    if (isRender) {
                        delete timeline._head.fj_isIgnoreRender;
                    } else {
                        timeline._head.fj_isIgnoreRender = true;
                        isClearStyle && utils.cleanInlineStyles(timeline);
                    }
                }
            }
        });
    }
    _updateAnimationContainerStyle(aniName, currentTime) {
        if (this._animationContainer) {
            if (this._isStaticAnimeByTime && currentTime !== undefined && !this._isStaticAnimeByTime(currentTime)) {
                return;
            }

            const isClip = this._isWillBeClipContainer(aniName);
            this._animationContainer.style.overflow = isClip ? 'hidden' : 'visible';
            const willChange = this._isWillChangeTransformContainer(aniName);
            this._animationContainer.style.willChange = willChange ? 'transform' : 'unset';
        }
    }
    /**
     * @desc 调整动画数据
     */
    _adjustAnimeData(anime, size) {
        if (!anime) return;
        const newAnime = {};
        const { width, height } = size;

        Object.keys(anime).forEach(key => {
            switch (key) {
                case 'translateZ': {
                    const values = [...anime[key]];
                    values.forEach((value, index) => {
                        const newValue =
                            value.indexOf('vh') !== -1
                                ? (height * parseFloat(value)) / 100
                                : value.indexOf('vw') !== -1
                                ? (width * parseFloat(value)) / 100
                                : value;

                        values[index] = `${newValue}px`;
                    });
                    newAnime['translateZ'] = values;
                    break;
                }
                case 'perspective': {
                    const values = [...anime[key]];
                    values.forEach((value, index) => {
                        const newValue = width / parseFloat(value);

                        values[index] = `${newValue}px`;
                    });
                    newAnime['perspective'] = values;
                    break;
                }
                default: {
                    newAnime[key] = anime[key];
                    break;
                }
            }
        });
        return newAnime;
    }
    /**
     * @desc 删除该对象的所有动画
     * @param {[]} targets - 动画的目标
     */
    removeTargets(targets) {
        if (targets) {
            this._clearAnimationFromTarget(targets);
            utils.remove(targets);
        }
    }

    /**
     * @desc 销毁动画
     * @param {[]} targets - 该动画的目标元素数组
     */
    destroy(targets) {
        if (this._animation) {
            this._animation.cancel();
            this._animationTimeLines.forEach(data => {
                data.timeline.cancel();
                utils.cleanInlineStyles(data.timeline);
                data.timeline.remove(targets);
            });
            utils.cleanInlineStyles(this._animation);
            this._animation.remove(targets);
            this._animation = null;
        }

        this._animationTimeLines = [];
        this.removeTargets(targets);
        this._destroyCallbacks.forEach(callback => callback && callback());
        this._destroyCallbacks = [];
        this._animationContainer = null;
    }

    isLoop() {
        return this._isLoop;
    }

    /**
     * @desc 是否播放完毕
     * @return {boolean}
     */
    isCompleted() {
        return this._animation && this._animation.completed;
    }

    /**
     * @desc 是否是暂停的状态
     * @return {*}
     */
    isPaused() {
        return this._animation && this._animation.paused;
    }

    /**
     * @desc 播放
     */
    play() {
        if (this._animation) this._animation.play();
    }
    pause() {
        if (this._animation) this._animation.pause();
    }

    restart() {
        this._animation.resume();
    }

    /**
     * @desc 跳转到某一时间点
     * @param {number} time - 时间，单位为毫秒
     */
    seek(time, options = {}) {
        const { isRealFrameExport = false } = options;

        this.seekAnimation(this._animation, time);
        // TODO: 输出封面帧时单次 seek 动画错误
        isRealFrameExport && this.seekAnimation(this._animation, time);

        // 如果seek到0，不会触发更新方法，则需要手动触发
        if (time === 0) {
            this._onBeforeUpdate({ currentTime: 0 }, this._animationTargets, {});
            this._onUpdate({ currentTime: 0 }, this._animationTargets, {});
        }
    }

    /**
     * @desc 统一animejs的seek入口，在这里可以做统一处理
     * @param {*} animation
     * @param {*} time
     */
    seekAnimation(animation, time) {
        if (animation) {
            // trick
            // 减少动画尾部的时间误差，解决动画文字封面帧渲染存在文字显示不完整的问题
            const currentTime = Math.ceil(time * 1000) / 1000;
            animation.seek(currentTime);
        }
    }

    /**
     * @desc 反转动画
     */
    reverse() {
        if (this._animation) this._animation.reverse();
    }

    /**
     * @desc 复原动画
     */
    reset() {
        if (this._animation) {
            this._animation.cancel();
            this._animation.revert();
            this._clearAnimationFromTarget(this._animationTargets);
            this._animationTimeLines.forEach(data => {
                data.timeline.cancel();
                data.timeline.revert();
            });
        }
    }

    /**
     * @description 返回当前时间
     * @return {*}
     */
    getCurrentTime() {
        return this._animation.currentTime;
    }

    getDuration() {
        return this._animation.duration;
    }

    /**
     * @desc 该动画是否需要裁剪容器
     * @param animationName - 动画的名字
     * @return {boolean}
     * @private
     */
    _isWillBeClipContainer(animationName) {
        if (!animationName) return false;
        return WIPE_ANIMATION_NAMES.indexOf(animationName) !== -1;
    }

    /**
     * @desc 该动画是否需要设置backfaceVisibility
     * @param animationName - 动画的名字
     * @return {boolean}
     * @private
     */
    _isBackfaceVisibility(animationName) {
        return BACKFACE_VISIBILITY_ANIMATION_NAMES.indexOf(animationName) !== -1;
    }
    /**
     * @desc 该动画是否需要田间 will-change
     * @param animationName - 动画的名字
     * @return {boolean}
     * @private
     */
    _isWillChangeTransformContainer(animationName) {
        if (!animationName) return false;
        const {
            ZOOM_IN,
            ZOOM_OUT,
            POPUP_OUT,
            BREATH_IN,
            BREATH_OUT,
            ALL_FLIP_Y_LOOP,
            ALL_FLIP_X_LOOP,
            ALL_SPIN_3D_LOOP,
            ALL_SPIN_3D_VERTICAL_LOOP,
            ALL_SWAY_3D_LOOP,
            ALL_SWAY_3D_VERTICAL_LOOP,
            ALL_SPIN_SMOOTH_LOOP,
            ALL_SPIN_3D_IN,
            ALL_SPIN_3D_OUT,
        } = ANIMATION_NAME;

        return (
            animationName === ZOOM_IN ||
            animationName === ZOOM_OUT ||
            animationName === POPUP_OUT ||
            animationName === BREATH_IN ||
            animationName === BREATH_OUT ||
            animationName === ALL_FLIP_Y_LOOP ||
            animationName === ALL_FLIP_X_LOOP ||
            animationName === ALL_SPIN_3D_LOOP ||
            animationName === ALL_SPIN_3D_VERTICAL_LOOP ||
            animationName === ALL_SWAY_3D_LOOP ||
            animationName === ALL_SWAY_3D_VERTICAL_LOOP ||
            animationName === ALL_SPIN_SMOOTH_LOOP ||
            animationName === ALL_SPIN_3D_IN ||
            animationName === ALL_SPIN_3D_OUT
        );
    }

    /**
     * @desc 根据动画的名字获取相对应的transforms动画数据
     * @param animationName
     * @param {{}} options - 可用的数据
     * @param {{}} options.moveData - 动画目标的相对rect
     * @private
     * @return {{}} data - 返回动画数据
     */
    _createAnimationData(animationName, options) {
        if (!FJMotionData[animationName]) return null;
        let data = {};
        if (options && options.moveData) {
            switch (animationName) {
                case ANIMATION_NAME.MOVE_IN_LEFT:
                    data = { translateX: [-options.moveData.left * 100 + '%', '0%'] };
                    break;
                case ANIMATION_NAME.MOVE_OUT_LEFT: {
                    data = { translateX: ['0%', -options.moveData.left * 100 + '%'] };
                    break;
                }
                case ANIMATION_NAME.MOVE_IN_RIGHT:
                    data = { translateX: [options.moveData.right * 100 + '%', '0%'] };
                    break;
                case ANIMATION_NAME.MOVE_OUT_RIGHT: {
                    data = { translateX: ['0%', options.moveData.right * 100 + '%'] };
                    break;
                }
                case ANIMATION_NAME.MOVE_IN_UP:
                    data = { translateY: [-options.moveData.top * 100 + '%', '0%'] };
                    break;
                case ANIMATION_NAME.MOVE_OUT_UP: {
                    data = { translateY: ['0%', -options.moveData.top * 100 + '%'] };
                    break;
                }
                case ANIMATION_NAME.MOVE_IN_BOTTOM:
                    data = { translateY: [options.moveData.bottom * 100 + '%', '0%'] };
                    break;
                case ANIMATION_NAME.MOVE_OUT_BOTTOM: {
                    data = { translateY: ['0%', options.moveData.bottom * 100 + '%'] };
                    break;
                }
                case ANIMATION_NAME.MOVE_IN_LEFT_UP:
                    data = {
                        translateX: [-options.moveData.left * 100 + '%', '0%'],
                        translateY: [-options.moveData.top * 100 + '%', '0%'],
                    };
                    break;
                case ANIMATION_NAME.MOVE_OUT_LEFT_UP: {
                    data = {
                        translateX: ['0%', -options.moveData.left * 100 + '%'],
                        translateY: ['0%', -options.moveData.top * 100 + '%'],
                    };
                    break;
                }
                case ANIMATION_NAME.MOVE_IN_LEFT_BOTTOM:
                    data = {
                        translateX: [-options.moveData.left * 100 + '%', '0%'],
                        translateY: [options.moveData.bottom * 100 + '%', '0%'],
                    };
                    break;
                case ANIMATION_NAME.MOVE_OUT_LEFT_BOTTOM: {
                    data = {
                        translateX: ['0%', -options.moveData.left * 100 + '%'],
                        translateY: ['0%', options.moveData.bottom * 100 + '%'],
                    };
                    break;
                }
                case ANIMATION_NAME.MOVE_IN_RIGHT_UP:
                    data = {
                        translateX: [options.moveData.right * 100 + '%', '0%'],
                        translateY: [-options.moveData.top * 100 + '%', '0%'],
                    };
                    break;
                case ANIMATION_NAME.MOVE_OUT_RIGHT_UP: {
                    data = {
                        translateX: ['0%', options.moveData.right * 100 + '%'],
                        translateY: ['0%', -options.moveData.top * 100 + '%'],
                    };
                    break;
                }
                case ANIMATION_NAME.MOVE_IN_RIGHT_BOTTOM:
                    data = {
                        translateX: [options.moveData.right * 100 + '%', '0%'],
                        translateY: [options.moveData.bottom * 100 + '%', '0%'],
                    };
                    break;
                case ANIMATION_NAME.MOVE_OUT_RIGHT_BOTTOM: {
                    data = {
                        translateX: ['0%', options.moveData.right * 100 + '%'],
                        translateY: ['0%', options.moveData.bottom * 100 + '%'],
                    };
                    break;
                }
                case ANIMATION_NAME.TUMBLE_LEFT_IN: {
                    data = {
                        translateX: [-options.moveData.left * 100 + '%', 0],
                    };
                    break;
                }
                case ANIMATION_NAME.TUMBLE_RIGHT_IN: {
                    data = {
                        translateX: [options.moveData.right * 100 + '%', 0],
                    };
                    break;
                }
                case ANIMATION_NAME.TUMBLE_LEFT_OUT: {
                    data = {
                        translateX: [0, -options.moveData.left * 100 + '%'],
                    };
                    break;
                }
                case ANIMATION_NAME.TUMBLE_RIGHT_OUT: {
                    data = {
                        translateX: [0, options.moveData.right * 100 + '%'],
                    };
                    break;
                }
                case ANIMATION_NAME.STOMP_IN: {
                    data = {
                        scale: [Math.max(1 / options.moveData.width, 1 / options.moveData.height), 1],
                    };
                    break;
                }
                case ANIMATION_NAME.STOMP_OUT: {
                    data = {
                        scale: [1, Math.max(1 / options.moveData.width, 1 / options.moveData.height)],
                    };
                    break;
                }
                case ANIMATION_NAME.SUBTITLE_SCROLL_DOWN_LOOP_ONCE:
                case ANIMATION_NAME.SUBTITLE_SCROLL_DOWN_LOOP:
                    data = { translateY: [options.moveData.bottom * 100 + '%', -options.moveData.top * 100 + '%'] };
                    break;
                case ANIMATION_NAME.BALLOON_RANDOM_LOOP:
                    const { startTime, endTime } = options;
                    const length = Math.max(2, Math.floor((endTime - startTime) / 1400));

                    const customKeyframes = FJMotionData[animationName].methods.customKeyframes({
                        length,
                    });
                    data = {
                        keyframes: customKeyframes,
                    };
                    break;
                default:
                    break;
            }
        }

        const motionData = FJMotionData[animationName];

        return { ...motionData, ...data };
    }
    /**r
     * @desc 更新target的style
     * @param {[]} targets
     * @param animationName
     */
    _updateTransformOrigin(targets, animationName) {
        if (!targets) return;

        switch (animationName) {
            case ANIMATION_NAME.ZOOM_IN_UP:
            case ANIMATION_NAME.ZOOM_OUT_DOWN:
            case ANIMATION_NAME.POPUP_IN_UP:
            case ANIMATION_NAME.POPUP_OUT_DOWN: {
                targets.forEach(target => {
                    target.style.transformOrigin = 'center bottom';
                });
                break;
            }

            case ANIMATION_NAME.ZOOM_IN_DOWN:
            case ANIMATION_NAME.ZOOM_OUT_UP:
            case ANIMATION_NAME.POPUP_IN_DOWN:
            case ANIMATION_NAME.POPUP_OUT_UP: {
                targets.forEach(target => {
                    target.style.transformOrigin = 'center top';
                });
                break;
            }

            case ANIMATION_NAME.ZOOM_IN_LEFT:
            case ANIMATION_NAME.ZOOM_OUT_LEFT:
            case ANIMATION_NAME.POPUP_IN_LEFT:
            case ANIMATION_NAME.POPUP_OUT_LEFT: {
                targets.forEach(target => {
                    target.style.transformOrigin = 'left center';
                });
                break;
            }
            case ANIMATION_NAME.ZOOM_IN_RIGHT:
            case ANIMATION_NAME.POPUP_IN_RIGHT:
            case ANIMATION_NAME.ZOOM_OUT_RIGHT:
            case ANIMATION_NAME.POPUP_OUT_RIGHT: {
                targets.forEach(target => {
                    target.style.transformOrigin = 'right center';
                });
                break;
            }

            case ANIMATION_NAME.POPUP_IN_LEFT_UP:
            case ANIMATION_NAME.POPUP_OUT_LEFT_UP:
                targets.forEach(target => {
                    target.style.transformOrigin = 'left top';
                });
                break;
            case ANIMATION_NAME.POPUP_IN_LEFT_DOWN:
            case ANIMATION_NAME.POPUP_OUT_LEFT_DOWN:
                targets.forEach(target => {
                    target.style.transformOrigin = 'left bottom';
                });
                break;
            case ANIMATION_NAME.POPUP_IN_RIGHT_UP:
            case ANIMATION_NAME.POPUP_OUT_RIGHT_UP:
                targets.forEach(target => {
                    target.style.transformOrigin = 'right top';
                });
                break;
            case ANIMATION_NAME.POPUP_IN_RIGHT_DOWN:
            case ANIMATION_NAME.POPUP_OUT_RIGHT_DOWN:
                targets.forEach(target => {
                    target.style.transformOrigin = 'right bottom';
                });
                break;
            default:
                targets.forEach(target => {
                    target.style.transformOrigin = 'center';
                });
                break;
        }
    }

    /**
     * @desc 清空targets中的样式
     * @param targets
     * @protected
     */
    _clearAnimationFromTarget(targets = []) {
        if (targets) {
            targets.forEach(item => {
                if (item) {
                    item.style.opacity = 1;
                    item.style.transformOrigin = '';
                    item.style.transform = 'none';
                    item.style.filter = 'none';
                    item.style['clip-path'] = '';
                }
            });
        }
    }
    /**
     * @desc 是否是有效动画
     * @param name
     * @return {boolean}
     */
    static isEffectiveAnimation(name) {
        if (!name || !FJMotionData[name]) return false;
        else return true;
    }
    /**
     * @desc 是否是循环动画
     * @param name
     * @return {boolean}
     */
    static isLoopAnimation(name) {
        if (!name || !FJMotionData[name]) return false;
        return Boolean(FJMotionData[name].isLoop);
    }

    static getCoverFrameTime(name, duration) {
        if (!name || !FJMotionData[name]) return 0;
        let coverFrameRate = FJMotionData[name].coverFrameRate;
        if (typeof coverFrameRate !== 'undefined') return duration * coverFrameRate;
        else return 0;
    }

    /**
     * @desc 返回是否需要动态更新数据的动画
     * @param name
     * @return {boolean}
     */
    static isDynamicAnimation(name) {
        return DYNAMIC_ANIMATION_NAMES.indexOf(name) !== -1;
    }
}

export default Animation;
export { ANIMATION_NAME, DEFAULT_ANIMATION_DURATION };
